import React, { useEffect, useState } from 'react';
import { Download, RefreshCw, X, Share } from 'lucide-react';
import { onUpdateAvailable, applyUpdate, isStandalone, isIos } from '../pwa';
import { useApp } from '../context/AppContext';

const DISMISS_KEY = 'ac_install_dismissed';

// Two small banners that make the site behave like an installed app:
//
//  - "Nouvelle version" — the important one. A service worker will otherwise
//    keep serving the bundle it cached, and this menu changes every week.
//  - "Installer" — Android fires beforeinstallprompt and we can show a real
//    install button. Safari never fires it, so iOS gets the manual recipe
//    instead; without that, iPhone users simply never discover the feature.
export default function PwaPrompts() {
  const { t } = useApp();
  const [updateReady, setUpdateReady] = useState(false);
  const [deferred, setDeferred] = useState(null);
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch (e) { return false; }
  });

  useEffect(() => onUpdateAvailable(setUpdateReady), []);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    const onInstalled = () => { setDeferred(null); hide(); };
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const hide = () => {
    setDismissed(true);
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch (e) { /* private mode */ }
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      try { await deferred.userChoice; } catch (e) { /* ignore */ }
      setDeferred(null);
      hide();
    } else {
      setShowIosHelp(true);
    }
  };

  // Already installed: nothing to offer but the update banner.
  const canOfferInstall = !isStandalone() && !dismissed && (deferred || isIos());

  if (!updateReady && !canOfferInstall) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] px-3 pb-[calc(12px+env(safe-area-inset-bottom))] pointer-events-none">
      <div className="max-w-[560px] mx-auto space-y-2">
        {updateReady && (
          <div data-testid="pwa-update-banner"
               className="pointer-events-auto flex items-center gap-3 bg-[#2f3826] text-white rounded-xl shadow-lg px-4 py-3">
            <RefreshCw size={17} className="shrink-0" />
            <p className="flex-1 text-sm">{t('pwa_update')}</p>
            <button onClick={applyUpdate} data-testid="pwa-update-apply"
                    className="shrink-0 bg-white text-[#2f3826] text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-neutral-100 transition-colors">
              {t('pwa_update_action')}
            </button>
          </div>
        )}

        {canOfferInstall && !showIosHelp && (
          <div data-testid="pwa-install-banner"
               className="pointer-events-auto flex items-center gap-3 bg-white border border-[#d8dcc6] rounded-xl shadow-lg px-4 py-3">
            <img src="/icons/icon-192.png" alt="" className="w-9 h-9 rounded-lg shrink-0" />
            <p className="flex-1 text-sm text-[#2f3826] leading-snug">{t('pwa_install')}</p>
            <button onClick={install} data-testid="pwa-install-action"
                    className="shrink-0 flex items-center gap-1.5 bg-[#4b5e34] hover:bg-[#3f5230] text-white text-sm font-medium px-3 py-1.5 rounded-lg transition-colors">
              <Download size={15} /> {t('pwa_install_action')}
            </button>
            <button onClick={hide} aria-label="Fermer" className="shrink-0 text-neutral-400 hover:text-neutral-600">
              <X size={17} />
            </button>
          </div>
        )}

        {showIosHelp && (
          <div data-testid="pwa-ios-help"
               className="pointer-events-auto bg-white border border-[#d8dcc6] rounded-xl shadow-lg px-4 py-3.5">
            <div className="flex items-start gap-3">
              <img src="/icons/icon-192.png" alt="" className="w-9 h-9 rounded-lg shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-[#2f3826] mb-1">{t('pwa_ios_title')}</p>
                <p className="text-sm text-neutral-600 flex items-center gap-1.5 flex-wrap">
                  <span>1.</span> <Share size={15} className="text-[#4b5e34]" /> <span>{t('pwa_ios_step1')}</span>
                </p>
                <p className="text-sm text-neutral-600 mt-0.5">2. {t('pwa_ios_step2')}</p>
              </div>
              <button onClick={() => { setShowIosHelp(false); hide(); }} aria-label="Fermer"
                      className="shrink-0 text-neutral-400 hover:text-neutral-600">
                <X size={17} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
